import { getEverypowerPawnLocalImage } from '@/lib/everypower-data';
import { xatSmilieImageUrl } from '@/services/api-paths';
import {
  GS_XATBLOG_BASE,
  GS_XAT_COM_BASE,
  normalizeSmileyCode,
  normalizeSmileySizePx,
  smileyStripUrl,
  xatPowerIconUrl,
  xatPowerIconUrlPlain,
  xatComPowerIconUrl,
  xatComPowerIconUrlPlain,
  SMILEY_ALLOWED_SIZES,
  type SmileyAllowedSizePx,
} from '@/lib/xat-smilie-urls';

export {
  GS_XATBLOG_BASE,
  GS_XAT_COM_BASE,
  normalizeSmileyCode,
  normalizeSmileySizePx,
  smileyStripUrl,
  xatPowerIconUrl,
  xatPowerIconUrlPlain,
  xatComPowerIconUrl,
  xatComPowerIconUrlPlain,
  SMILEY_ALLOWED_SIZES,
  type SmileyAllowedSizePx,
};

export type SmileyImageSource = 'power' | 'strip';

/** Proxy same-origin / API — Content-Type correto (evita ERR_BLOCKED_BY_ORB no CDN). */
export function localSmilieProxyUrl(code: string, sizePx: number = 40): string {
  return xatSmilieImageUrl({
    name: normalizeSmileyCode(code),
    size: String(normalizeSmileySizePx(sizePx)),
  });
}

/** Códigos internos de peão (ex.: `p1emerald*ff69b4`) — pré-visualização em gs.xatblog.net. */
export function isPawnInternalCode(code: string): boolean {
  const c = code.trim().toLowerCase();
  return c.startsWith('p1') || (c.includes('*') && !c.includes('#'));
}

/** Combinações de chat (ex.: smile#r, todull#tohypno#…). */
export function isSmileyComboCode(code: string): boolean {
  return normalizeSmileyCode(code).includes('#');
}

/** Candidatos de URL para smilie ou peão (peões priorizam gs.xatblog.net). */
export function smilieOrPawnImageCandidates(
  code: string,
  sizePx: number = 40,
): string[] {
  const normalizedSize = normalizeSmileySizePx(sizePx);
  const local = getEverypowerPawnLocalImage(code);
  const source: SmileyImageSource = isPawnInternalCode(code) ? 'power' : 'strip';
  const remote = xatSmilieImageCandidates(code, normalizedSize, source);
  if (!local) return remote;
  return Array.from(new Set([local, ...remote]));
}

/**
 * URLs de imagem do xat.
 * Proxy primeiro (evita ORB). CDN só com path «plain» — `?.png` devolve webp e o Chrome bloqueia.
 */
export function xatSmilieImageCandidates(
  code: string,
  sizePx: number = 40,
  source: SmileyImageSource = 'power',
): string[] {
  const normalized = normalizeSmileyCode(code);
  const px = normalizeSmileySizePx(sizePx);
  const combo = isSmileyComboCode(normalized);

  // Combos: proxy same-origin primeiro (CDN directo no browser → ORB/403).
  if (combo) {
    const sizes = Array.from(new Set([px, 40, 20])) as SmileyAllowedSizePx[];
    const urls: string[] = [];
    for (const size of sizes) {
      urls.push(localSmilieProxyUrl(normalized, size));
    }
    for (const size of sizes) {
      urls.push(xatComPowerIconUrlPlain(normalized, size));
      urls.push(xatPowerIconUrlPlain(normalized, size));
    }
    urls.push(smileyStripUrl(normalized, 40));
    return Array.from(new Set(urls));
  }

  const proxy = localSmilieProxyUrl(normalized, px);
  const blogPlain = xatPowerIconUrlPlain(normalized, px);
  const blogStrip = smileyStripUrl(normalized, px);

  if (source === 'strip') {
    return Array.from(new Set([proxy, blogStrip, blogPlain]));
  }
  return Array.from(new Set([proxy, blogPlain, blogStrip]));
}

/** Extrai o nome interno e monta a URL de pré-visualização (equivalente a GsImage). */
export function xatSmilieImageUrlFromParensCode(codeCell: string): string {
  const trimmed = codeCell.trim();
  const inner = /^\(([^)]+)\)$/.exec(trimmed)?.[1]?.trim() ?? trimmed.replace(/^\(|\)$/g, '');
  return localSmilieProxyUrl(inner);
}

function escapeHtmlAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/** HTML seguro para coluna «RESULTADO» em tabelas de guias. */
export function xatSmilieResultImgHtml(codeWithParens: string): string {
  const url = xatSmilieImageUrlFromParensCode(codeWithParens);
  const alt = escapeHtmlAttr(codeWithParens.trim());
  return `<img src="${escapeHtmlAttr(url)}" alt="${alt}" width="36" height="36" class="inline-block h-9 w-9 object-contain align-middle" loading="lazy" decoding="async" />`;
}
